import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import {
  KeyRound,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  UserPlus,
  History,
  GraduationCap,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  adminGrantAccess,
  adminListAccess,
  adminRevokeAccess,
  type AdminAccessLogRow,
  type AdminRole,
  type AdminUserRow,
} from "@/lib/admin-access.functions";
import {
  adminGrantCustomAccess,
  adminListCustomAccess,
  adminRevokeCustomAccess,
  type CustomAccessPlan,
  type CustomAccessRow,
  type CustomAccessType,
} from "@/lib/custom-access.functions";

export const Route = createFileRoute("/admin/access")({
  head: () => ({
    meta: [
      { title: "Admin Access Controls - WeLivdIt Admin" },
      { name: "description", content: "Manage authorized administrator emails and monitor access logs." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminAccessPage,
});

const ROLE_LABEL: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  moderator: "Moderator",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AdminAccessPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<"loading" | "ok" | "denied">("loading");

  const listFn = useServerFn(adminListAccess);
  const grantFn = useServerFn(adminGrantAccess);
  const revokeFn = useServerFn(adminRevokeAccess);

  const [admins, setAdmins] = useState<AdminUserRow[]>([]);
  const [logs, setLogs] = useState<AdminAccessLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("admin");
  const [granting, setGranting] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingRevoke, setPendingRevoke] = useState<AdminUserRow | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/access" } });
        return;
      }
      setGate((await hasAdminAccess(sess.session)) ? "ok" : "denied");
    })();
  }, [navigate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFn();
      setAdmins(data.admins);
      setLogs(data.logs);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load admin access");
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  useEffect(() => {
    if (gate === "ok") void refresh();
  }, [gate, refresh]);

  const handleGrant = async () => {
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) return toast.error("Enter a valid email address");
    if (admins.some((a) => a.email.toLowerCase() === value)) {
      return toast.error("That email already has admin access");
    }
    setGranting(true);
    try {
      const res = await grantFn({ data: { email: value, role } });
      toast.success(
        res.status === "active"
          ? `${value} now has ${ROLE_LABEL[role]} access`
          : `${value} is pending — access activates when they sign up`,
      );
      setEmail("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not grant access");
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    try {
      await revokeFn({ data: { id: pendingRevoke.id } });
      toast.success(`Access revoked for ${pendingRevoke.email}`);
      setPendingRevoke(null);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not revoke access");
    } finally {
      setRevoking(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) => a.email.toLowerCase().includes(q));
  }, [admins, search]);

  const activeCount = admins.filter((a) => a.status === "active").length;

  if (gate === "denied")
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <p className="text-sm text-muted-foreground">You don't have access to this page.</p>
      </div>
    );

  return (
    <AdminShell
      title="Admin Access Controls"
      description="Manage authorized administrator emails and monitor access logs."
      actions={
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      }
    >
      {/* Overview */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" /> Active admins
          </div>
          <p className="mt-2 font-display text-3xl font-semibold tabular-nums">{activeCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {activeCount === 1 ? "1 Active Admin" : `${activeCount} Active Admins`}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <UserPlus className="h-4 w-4 text-primary" /> Pending invites
          </div>
          <p className="mt-2 font-display text-3xl font-semibold tabular-nums">
            {admins.filter((a) => a.status === "pending").length}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Activate automatically on sign-up</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <History className="h-4 w-4 text-primary" /> Recent actions
          </div>
          <p className="mt-2 font-display text-3xl font-semibold tabular-nums">{logs.length}</p>
          <p className="mt-1 text-xs text-muted-foreground">Last 30 audit entries</p>
        </div>
      </div>

      {/* Grant access */}
      <div className="mt-6 grid gap-6 xl:grid-cols-2 xl:items-start">
      <section className="rounded-2xl border border-border bg-card p-6">

        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Admin Console Authorization</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Authorize an email address to open the admin console. If they haven't signed up yet, access activates the
          moment they create their account.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px_200px] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="grant-email">User email</Label>
            <Input
              id="grant-email"
              type="email"
              maxLength={255}
              placeholder="Enter user email…"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleGrant();
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="grant-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AdminRole)}>
              <SelectTrigger id="grant-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="moderator">Moderator</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleGrant} disabled={granting} className="mt-4 gap-2">
          {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Grant Access
        </Button>
      </section>

      <CustomAccessCard />
      </div>



      {/* Active admins */}
      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-semibold">Active admins</h2>
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          {loading ? (
            <div className="grid place-items-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No admins match this search.</p>
          ) : (
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">User email</th>
                  <th className="py-2 pr-4 font-medium">Role</th>
                  <th className="py-2 pr-4 font-medium">Granted</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b border-border/60 last:border-0">
                    <td className="py-3 pr-4 font-medium">{a.email}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{ROLE_LABEL[a.role] ?? a.role}</td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {a.granted_at ? format(parseISO(a.granted_at), "d MMM yyyy") : "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={
                          a.status === "active"
                            ? "inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                            : "inline-flex items-center rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                        }
                      >
                        {a.status === "active" ? "Active" : "Pending"}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingRevoke(a)}>
                        <ShieldOff className="h-3.5 w-3.5" /> Revoke Access
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Audit trail */}
      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Access activity log</h2>
        </div>
        {logs.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No access changes recorded yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {logs.map((l) => (
              <li key={l.id} className="flex items-start gap-3 text-sm">
                <span
                  className={
                    l.action === "revoked"
                      ? "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-destructive"
                      : "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                  }
                />
                <div>
                  <p>
                    {l.action === "granted" ? (
                      <>
                        <span className="font-medium">{l.actor_email ?? "An admin"}</span> granted{" "}
                        {ROLE_LABEL[(l.role ?? "admin") as AdminRole] ?? l.role} access to{" "}
                        <span className="font-medium">{l.target_email}</span>
                      </>
                    ) : (
                      <>
                        Access revoked for <span className="font-medium">{l.target_email}</span>
                        {l.actor_email ? ` by ${l.actor_email}` : ""}
                      </>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(parseISO(l.created_at), { addSuffix: true })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={!!pendingRevoke} onOpenChange={(o) => !o && setPendingRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke admin access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke admin access for {pendingRevoke?.email}? They will lose access to the
              admin console immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleRevoke();
              }}
              disabled={revoking}
            >
              {revoking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}

function CustomAccessCard() {
  const listFn = useServerFn(adminListCustomAccess);
  const grantFn = useServerFn(adminGrantCustomAccess);
  const revokeFn = useServerFn(adminRevokeCustomAccess);

  const [rows, setRows] = useState<CustomAccessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [planName, setPlanName] = useState<CustomAccessPlan>("Basic Support");
  const [accessType, setAccessType] = useState<CustomAccessType>("standard");
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listFn();
      setRows(res.rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load custom access list");
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleGrant = async () => {
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) return toast.error("Enter a valid email address");
    setSaving(true);
    try {
      await grantFn({ data: { email: value, planName, accessType } });
      toast.success(`${value} now has ${planName} dashboard access`);
      setEmail("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not grant access");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (row: CustomAccessRow) => {
    setRevokingId(row.id);
    try {
      await revokeFn({ data: { id: row.id } });
      toast.success(`Custom access revoked for ${row.email}`);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not revoke access");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <GraduationCap className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg font-semibold">Grant Custom Student Access</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter a student email address to immediately grant test/paid access to the custom dashboard.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px_200px] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="custom-email">User email</Label>
          <Input
            id="custom-email"
            type="email"
            maxLength={255}
            placeholder="User email…"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleGrant();
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="custom-plan">Select plan</Label>
          <Select value={planName} onValueChange={(v) => setPlanName(v as CustomAccessPlan)}>
            <SelectTrigger id="custom-plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Basic Support">Basic Support</SelectItem>
              <SelectItem value="Full Support">Full Support</SelectItem>
              <SelectItem value="Refusal Support">Refusal Support</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="custom-access-type">Access type</Label>
          <Select value={accessType} onValueChange={(v) => setAccessType(v as CustomAccessType)}>
            <SelectTrigger id="custom-access-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard Paid Access</SelectItem>
              <SelectItem value="testing">Test / Beta Access</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Standard Paid Access mirrors the live student dashboard. Test / Beta Access additionally unlocks the
        experimental Calendar tab and custom dashboard layout.
      </p>
      <Button onClick={handleGrant} disabled={saving} className="mt-4 gap-2">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
        Grant Testing / Paid Access
      </Button>

      <div className="mt-6">
        <h3 className="text-sm font-semibold">Active Granted Students</h3>
        {loading ? (
          <div className="grid place-items-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">No students have custom dashboard access yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Access Type</th>
                  <th className="py-2 pr-4 font-medium">Granted</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="py-3 pr-4 font-medium">{r.email}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{r.plan_name}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={
                          (r.access_type ?? "standard") === "testing"
                            ? "inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700"
                            : "inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
                        }
                      >
                        {(r.access_type ?? "standard") === "testing" ? "Test / Beta Access" : "Standard Paid"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {r.granted_at ? format(parseISO(r.granted_at), "d MMM yyyy") : "—"}
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={revokingId === r.id}
                        onClick={() => void handleRevoke(r)}
                      >
                        {revokingId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <ShieldOff className="h-3.5 w-3.5" />
                        )}
                        Revoke Access
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
